import React, { useCallback, useState } from "react";

export interface SelectOption {
  id: string | number;
  label?: string;
}

interface DropdownOptionProps<T extends SelectOption> {
  index: number;
  selected: boolean;
  option: T;
  onSelect: (option: T) => void;
}

const DropdownOption = <T extends SelectOption>({
  index,
  selected,
  option,
  onSelect,
}: DropdownOptionProps<T>) => {
  const scrollIfSelected = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && selected) {
        node.scrollIntoView({
          block: "center",
          inline: "center",
          behavior: "smooth",
        });
      }
    },
    [selected]
  );

  const onClick = useCallback(() => {
    onSelect(option);
  }, [option, onSelect]);

  return (
    <div
      ref={scrollIfSelected}
      key={index}
      role="option"
      className={`raceweekSelector__options__item ${
        selected ? "raceweekSelector__options__item--selected" : ""
      }`}
      onClick={onClick}
    >
      <span>
        <span>{option.label ?? option.id}</span>
      </span>
    </div>
  );
};

interface SelectorDropdownProps<T extends SelectOption> {
  open: boolean;
  options: T[];
  selectOption: (option: T) => void;
  selected: T | undefined;
}

const Dropdown = <T extends SelectOption>({
  open,
  options,
  selectOption,
  selected,
}: SelectorDropdownProps<T>) => {
  return (
    <div
      role="listbox"
      aria-expanded={open}
      className={`raceweekSelector__options${
        open ? "" : " raceweekSelector__options--closed"
      }`}
    >
      {options.map((option, index) => {
        return (
          <DropdownOption
            index={index}
            key={index}
            selected={option.id === selected?.id}
            option={option}
            onSelect={selectOption}
          />
        );
      })}
    </div>
  );
};

interface DropdownSelectProps<T extends SelectOption> {
  options: T[];
  placeholder?: string;
  onSelect: (option: T) => void;
}

export const DropdownSelect = <T extends SelectOption>({
  options,
  placeholder,
  onSelect,
}: DropdownSelectProps<T>) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<T>();

  const toggleDropdown = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const selectOption = useCallback((option: T) => {
    setSelected(option);
    setOpen(false);
    onSelect(option);
  }, []);

  return (
    <div
      style={{ alignItems: "stretch" }}
      className="raceweekSelector__container"
    >
      <button
        className={[
          "raceweekSelector__button",
          "raceweekSelector__button--fullWidth",
          open ? "raceweekSelector__button--open" : "",
        ].join(" ")}
        onClick={toggleDropdown}
      >
        <div
          className={
            "raceweekSelector__button__text" +
            (selected ? "" : " raceweekSelector__button__text--placeholder")
          }
        >
          <span>{selected?.label ?? placeholder ?? "!!Select Option"}</span>
          <span className="raceweekSelector__button__icon" />
        </div>
      </button>
      <Dropdown
        open={open}
        options={options}
        selectOption={selectOption}
        selected={selected}
      />
    </div>
  );
};
